// src/pages/Dashboard.js
//
// ═══════════════════════════════════════════════════════════════════════════
//  CHANGE LOG — 2026-09-18
//
//  Everything below is about ONE thing: this page must never show a number
//  that was not measured. The backend was made honest earlier today
//  (services/cake.js v13, routes/revenue.js, services/everflow.js). This is
//  the frontend half.
//
//  1. buildPerf() / perfData — DELETED.
//       It built a 24-point chart out of Math.random() and was wired in as
//       `data={realPerf || perfData}`. That meant a random chart on every
//       page load until the fetch returned, and — because the catch block
//       only did console.error — a random chart FOREVER if the first fetch
//       failed. The chart now has three honest states: loading, error, data.
//
//  2. mkSpark() — REPLACED by buildSpark().
//       The old one drew `total x day / today` — a straight line. The
//       tooltip said "18th day of the month: $4,200" as though that were a
//       measurement; it was the month total scaled. Sparklines are now built
//       from real per-day rows returned by /api/revenue/chart.
//
//  3. Events and EVR cards — were hardcoded "0" / "0.00%" with a fake green
//       +0% badge and a fake sparkline. They now read real totals, which
//       routes/revenue.js was changed today to return (SUM(events)).
//
//  4. CVR percentage badge — guarded on THIS month's clicks while dividing
//       by LAST month's. Fixed.
//
//  5. Bar view drew clicks AND revenue bars whatever you had selected.
//       Now gated on `metrics`, exactly like the line view already was.
//
//  6. Metrics dropdown listed 11 options; only 4 were wired. Selecting one
//       of the other 7 silently blanked the chart. Trimmed to the 4 that
//       work — tell me if you want the rest wired properly instead.
//
//  7. A month with no revenue left the pie stuck on "Loading affiliate
//       data..." forever, because of `if (pieArr.length)`. Fixed.
//
//  NOT CHANGED: layout, colours, the pie, the CSS, the tooltips' styling.
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ComposedChart, Line
} from "recharts";
import "../styles/dashboard.css";
import {
  todayYMD,
  yesterdayYMD,
  edtParts,
  ymdToDisplayFull,
  addDaysYMD,
  edtTime,
  monthStartYMD,   // ADDED 2026-09-19 — for the like-for-like badge below
  monthEndYMD,     // ADDED 2026-09-19
} from "../utils/edt";

const API = process.env.REACT_APP_API_URL || "https://ipanel.idigitalhub.org/api";

/* ─────────────────────────────────────────────────────────────
   EDT-aware "today" values used in several places below.
   edtParts() gives us year/month/day/hour/dow in America/New_York
   time, regardless of where the browser is physically located.
   ───────────────────────────────────────────────────────────── */
const DAY_NAMES = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

/* ═════════════════════════════════════════════════════════════
   SPARKLINES — REAL DATA  (REPLACES mkSpark, 2026-09-18)

   WHAT WAS HERE BEFORE
     const mkSpark = (curTotal, lastTotal) => { ...
       const curVal = curT * (dayNum / todayCap);      // <- a formula
     }
   It took the month TOTAL and drew a straight line up to it. Day 12 of a
   month showing $4,200 did not mean $4,200 had been earned by day 12; it
   meant "the month total multiplied by twelve thirty-firsts". Every one of
   the six stat cards drew that same straight line, and the tooltip
   presented it as a measurement.

   WHAT HAPPENS NOW
     /api/revenue/chart?range=month       -> one row per day, this month
     /api/revenue/chart?range=last_month  -> one row per day, last month
   Each row carries running totals straight out of revenue_cache
   (clicks_cum, revenue_cum, conversions_cum, events_cum). We plot those.
   Day 12 now shows what had actually been earned by the end of day 12, and
   the last point of the current-month line equals the big number on the
   card — because they are the same data.

   Days that have not happened yet come back as NULL, so the line simply
   stops instead of being drawn flat along the bottom.
   ═════════════════════════════════════════════════════════════ */
const SPARK_DAYS = 31;

/**
 * Build one sparkline series from real per-day rows.
 *   curDays  — array of day rows for the current month  (may be empty)
 *   lastDays — array of day rows for last month         (may be empty)
 *   pick     — function that reads the cumulative value we want off a row
 *
 * Returns 31 points of { day, cur, last }. `null` means "no data for that
 * day", which Recharts draws as a gap rather than as a zero.
 */
function buildSpark(curDays, lastDays, pick) {
  return Array.from({ length: SPARK_DAYS }, (_, i) => {
    const c = curDays[i];
    const l = lastDays[i];
    const cv = c ? pick(c) : null;
    const lv = l ? pick(l) : null;
    return {
      day:  i + 1,
      cur:  (cv == null || Number.isNaN(cv)) ? null : parseFloat(Number(cv).toFixed(2)),
      last: (lv == null || Number.isNaN(lv)) ? null : parseFloat(Number(lv).toFixed(2)),
    };
  });
}

// The six things a sparkline can show. Each reads a cumulative field that
// routes/revenue.js was changed today to return.
const PICK_CLICKS = d => d.clicks_cum;
const PICK_REV    = d => d.revenue_cum;
const PICK_CONV   = d => d.conversions_cum;
const PICK_EVENTS = d => d.events_cum;
const PICK_CVR    = d => (d.clicks_cum > 0 ? (d.conversions_cum / d.clicks_cum) * 100 : 0);
const PICK_EVR    = d => (d.clicks_cum > 0 ? (d.events_cum      / d.clicks_cum) * 100 : 0);

// True when a series actually has something to draw. Used so a card can say
// "No daily data" instead of rendering an empty chart box that looks broken.
const sparkHasData = series =>
  Array.isArray(series) && series.some(p => p.cur != null || p.last != null);

/* ═════════════════════════════════════════════════════════════
   ADDED 2026-09-19 — THE PERCENTAGE BADGE, MADE LIKE-FOR-LIKE

   WHAT WAS WRONG
     The little green/red badge on each card compared
        "Current Month"  = the 1st up to TODAY        (e.g. 19 days)
     against
        "Last Month"     = the WHOLE previous month   (e.g. 31 days)

     Those are not comparable. A business doing exactly the same volume
     every single day would still show roughly -42% on the 19th, purely
     because 19 days is less than 31. Measured in production on 18 Sep: the
     badge read -84% on clicks, while the honest like-for-like figure
     (daily averages, 189/day vs 671/day) was -72%. Twelve points of that
     number was calendar, not performance.

   WHAT IT DOES NOW
     Compares the SAME SPAN of last month. On 19 September that is
     1–19 Sep against 1–19 Aug.

   THE "LAST MONTH" ROW AT THE BOTTOM OF EACH CARD IS UNCHANGED — it still
   shows the FULL previous month, because that is what its label says. The
   badge carries a hover tooltip naming its exact comparison window, so the
   two can never be confused.

   Month-length edge case is handled: on 31 March there is no 31 February,
   so the span is clamped to the last day that exists (28 or 29 Feb).
   ═════════════════════════════════════════════════════════════ */
const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/**
 * Given today's EDT parts, work out the matching span of last month.
 * Returns { start, end, label } where label reads like "1–19 Aug".
 */
function lastMonthSameSpan(ep) {
  const lmYear  = ep.month === 1 ? ep.year - 1 : ep.year;
  const lmMonth = ep.month === 1 ? 12          : ep.month - 1;   // 1-indexed

  const start   = monthStartYMD(lmYear, lmMonth);                // "2026-08-01"
  const fullEnd = monthEndYMD(lmYear, lmMonth);                  // "2026-08-31"

  // How many days last month actually had — 28/29/30/31.
  const lastDayOfLastMonth = parseInt(String(fullEnd).slice(8, 10), 10);

  // Clamp: today's day number, but never past the end of last month.
  // 31 Mar → 28 Feb (or 29 in a leap year). Without this the request would
  // ask for a date that does not exist.
  const day = Math.min(ep.day, lastDayOfLastMonth);

  const pad = n => String(n).padStart(2, "0");
  const end = `${lmYear}-${pad(lmMonth)}-${pad(day)}`;

  return {
    start,
    end,
    label: `vs 1–${day} ${MONTH_ABBR[lmMonth - 1]}`,
  };
}

/* ─────────────────────────────────────────────────────────────
   Metrics available on the Performance chart.

   TRIMMED 2026-09-18. The list used to hold 11 entries:
     cT, rT, cv, cvr, dc, ev, evr, ic, rtr, rpc, uc
   but only the first four are wired to anything. Picking "Duplicate
   Clicks" or "Unique Clicks" silently produced an empty chart — the option
   existed, the data path did not. Rather than leave dead options on screen,
   only the four that work are listed. The others can be added back once the
   backend returns them per hour and per day.
   ───────────────────────────────────────────────────────────── */
const ALL_METRICS = [
  {k:"cT",  l:"Clicks"},
  {k:"rT",  l:"Revenue"},
  {k:"cv",  l:"Approved Conversions"},
  {k:"cvr", l:"CVR"},
];

// Today's weekday name — EDT, not browser-local.
const _todayName = DAY_NAMES[edtParts().dow];

const TIME_OPTS = [
  {k:"ty", l:`Today vs Yesterday`},
  {k:"ts", l:`Today vs Last ${_todayName}`},
  {k:"ww", l:"Current Week vs Last Week"},
  {k:"mm", l:"Current Month vs Last Month"},
];

/* ═══════════════════════════════════════════════════════════════════════════
   ADDED 2026-09-24 — ONE PLACE FOR EVERY SERIES COLOUR ON THIS PAGE.

   WHY THIS EXISTS
     These colours used to be typed by hand in about twenty places — the line,
     the band behind it, the bar, the axis numbers, the tooltip label and the
     legend swatch were six separate copies of the same idea. They had already
     drifted apart: the legend box for "Yesterday Clicks" said
     rgba(189,213,246,0.75) while the band actually drawn was
     rgba(147,197,253,0.55). The legend was showing a colour that was not on
     the chart. Everything below now reads from this object, so they cannot
     disagree again, and changing a colour is a one-line change.

   THE RULE, taken from the reference design Aditi chose
     A band is its own line colour, made transparent. Nothing else.
     Her reference used 30–32%; she asked for deeper, so these are 46–48%.

   ── WHY THE OLD COLOURS LOOKED SO WASHED OUT (measured, not guessed) ──
     Recharts applies its OWN default fill opacity of 0.6 to an <Area> when
     you don't give it one. So fill="rgba(147,197,253,0.55)" was really being
     painted at 0.55 × 0.6 = 0.33. Sampling the pixels out of a screenshot of
     the live panel confirmed it exactly — #DFECFD on screen, which is what
     0.33 produces, not the 0.55 the code asked for.

     That is why every <Area> below now carries fillOpacity={1}. It is not
     decoration: without it these numbers get multiplied by 0.6 again and the
     chart stays as pale as it was.

   ── CV AND CVR ──
     Their LINE colours are deliberately unchanged. Only their bands moved to
     the 48% rule. The softer line colours here come from Aditi's reference
     image, which contains only Clicks and Revenue — inventing a matching
     green and purple would have been my guess rather than her choice.
   ═══════════════════════════════════════════════════════════════════════════ */
/* ── ADDED 2026-09-24 — the colour of the NUMBERS on the axes ───────────
   They used to take the series colour: #71A6E9 on the left, #F97316 on the
   right. Measured against a white card that is 2.5:1 and 3.0:1 contrast,
   where readable text needs 4.5:1 — which is exactly why they looked washed
   out on screen.

   A soft colour is right for a LINE. It is wrong for numbers you have to
   read. The tick numbers now use the same muted ink as the rest of the
   panel. The rotated "Clicks" and "Revenue" axis titles KEEP their series
   colour, because those identify a series rather than state a value.    */
const AXIS_INK = "var(--text-muted, #9CA3AF)";

const SERIES = {
  // Clicks — from the reference: line #71A6E9, band = that same colour at 48%
  clicks:  { line: "#71A6E9", band: "rgba(113,166,233,0.48)" },
  // Revenue — from the reference: line #E17836, band = that same colour at 46%
  revenue: { line: "#E17836", band: "rgba(225,120,54,0.46)"  },
  // CV — line unchanged (#16A34A); band is now that same green at 48%
  cv:      { line: "#16A34A", band: "rgba(22,163,74,0.48)"   },
  // CVR — line unchanged (#7C3AED); band is now that same violet at 48%
  cvr:     { line: "#7C3AED", band: "rgba(124,58,237,0.48)"  },
};

/* ─────────────────────────────────────────────────────────────
   Pie palette — infographic-style vibrant colors.
   First 6 colors are used for typical affiliate counts; cycles if more.
   ───────────────────────────────────────────────────────────── */
/* ── CHANGED 2026-09-24 — the pie palette ───────────────────────────────
   The old pastels were: #4FD1C5 teal, #F687B3 pink, #F6D060 yellow,
   #9F7AEA purple, #68D391 green, #FC8181 coral, #7FB5F0 blue, #F6AD55
   orange.

   Why they were replaced: run through a colourblindness check, the teal and
   the pink separated by only 3.1 where 8 is the minimum — and those are
   slot 1 and slot 2, your two biggest slices. To a red-green colourblind
   viewer CX3Ads and Instar were the same colour. Five of the eight were
   also too pale to sit on a white card.

   The set below keeps orange, pink and teal (Aditi's requirement) and
   passes every check: lightness band, chroma floor, colourblind separation
   on every adjacent pair, and normal-vision separation.

   Slot 1 is #E17836 on purpose — the EXACT orange of the Today Revenue
   line in the Performance chart beside it, so the two panels read as one
   design rather than two near-miss oranges.                             */
const PIE_COLORS = [
  "#E17836",  // orange  — slot 1 (biggest slice); matches the chart's line
  "#EC4899",  // pink    — slot 2
  "#3B82F6",  // blue    — slot 3
  "#14B8A6",  // teal    — slot 4
  "#A855F7",  // violet  — slot 5
  "#84CC16",  // lime    — slot 6
  "#EF4444",  // red     — slot 7
  "#6366F1",  // indigo  — slot 8
];

/* ── CHANGED 2026-09-24 — the layered "halo" geometry ───────────────────
   Was: RADII_CYCLE = [92, 87, 96, 90, 94, 87] — six values, so the pattern
   repeated after six wedges.

   Eight values now, with a wider spread, so the unevenness does not repeat
   itself around the circle. The reach is DECORATION: it follows the wedge's
   position, not its share, so a small wedge can sit further out than a big
   one. Reviewed on screen and chosen deliberately — the wedge ANGLE is what
   carries the real number, and that has always been correct.

   Everything from here out is fixed, which is the point: the inside is
   uneven, the outside is a clean circle.                                */
const RADII_CYCLE = [100, 88, 95, 84, 97, 90, 86, 93];
const INNER_R     = 34;        // was 27 — thicker colour band
const HUB_R       = 28;        // the white centre disc
const HALO_R0     = 122;       // the lighter echo arcs — same radius for all
const HALO_R1     = 132;
const HAIRLINES   = [106, 112, 134.5, 137];   // thin rings; two in the gap, two outside

/* A halo arc is its own wedge's colour at 45% — never a separate colour. */
function haloTint(hex) {
  const n = parseInt(String(hex).replace("#", ""), 16);
  if (Number.isNaN(n)) return "rgba(0,0,0,0.25)";
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},0.45)`;
}

/* ─────────────────────────────────────────────────────────────
   LayeredPie — unchanged apart from the empty-state message.
   Props: { hovered, onHover, pieData, totalRev, pieReady }
     pieData: [{ name, pct, rev, color }]
     totalRev: formatted string like "$26K"
     pieReady: false while still loading, true once a fetch has finished
   ───────────────────────────────────────────────────────────── */
function LayeredPie({ hovered, onHover, pieData, totalRev, pieReady }) {
  if (!pieData || !pieData.length) {
    // CHANGED 2026-09-18: this used to always read "Loading affiliate
    // data...". A month with no revenue therefore sat on "Loading..."
    // forever, because the caller only ever set pieData when it was
    // non-empty. Now the caller always sets it, and this tells the two
    // situations apart honestly.
    return (
      <div style={{padding:"48px 0",textAlign:"center",color:"var(--text-muted)",fontSize:12}}>
        {pieReady ? "No revenue recorded this month." : "Loading affiliate data..."}
      </div>
    );
  }

  /* CHANGED 2026-09-24: the canvas grew from 240 to 284 so the halo ring and
     the hairlines have somewhere to live. INNER_R moved to the constants at
     the top of the file, next to the other radii it has to agree with. */
  const CX = 142, CY = 142;
  const total = pieData.reduce((a, b) => a + b.pct, 0) || 1;

  /* One arc-path builder for both the wedges and the halo, so the two can
     never drift out of step. */
  const arcPath = (r0, r1, start, end) => {
    const large = (end - start) > Math.PI ? 1 : 0;
    const ox1 = r1 * Math.cos(start), oy1 = r1 * Math.sin(start);
    const ox2 = r1 * Math.cos(end),   oy2 = r1 * Math.sin(end);
    const ix1 = r0 * Math.cos(start), iy1 = r0 * Math.sin(start);
    const ix2 = r0 * Math.cos(end),   iy2 = r0 * Math.sin(end);
    return `M ${ox1.toFixed(2)} ${oy1.toFixed(2)} A ${r1} ${r1} 0 ${large} 1 ${ox2.toFixed(2)} ${oy2.toFixed(2)}`
         + ` L ${ix2.toFixed(2)} ${iy2.toFixed(2)} A ${r0} ${r0} 0 ${large} 0 ${ix1.toFixed(2)} ${iy1.toFixed(2)} Z`;
  };

  let angle = -Math.PI / 2;  // start at top
  const HALO_GAP = 0.035;    // a small breath either side of each halo arc
  const wedges = pieData.map((slice, i) => {
    const sweep = (slice.pct / total) * 2 * Math.PI;
    const start = angle;
    const end   = angle + sweep;
    const mid   = (start + end) / 2;
    angle = end;

    const R = RADII_CYCLE[i % RADII_CYCLE.length];

    /* ── CHANGED 2026-09-24 — where the % label goes, and whether it goes ──
       It used to sit at the halfway point between inner and outer, always.
       On a narrow wedge that does not fit: rendered and looked at, the 4%
       label spilled out over its neighbours, because a 4% wedge is 14° and
       at the mid radius that is only ~16px of arc for an 18px label.

       So: work out how much arc is actually available, and how much the
       text needs. Try the middle first; if it is tight, slide the label
       outwards where the wedge is wider. If even that will not hold it,
       drop the label — the legend underneath carries the number anyway,
       and a label spilling across a neighbour is worse than none.       */
    const fontSize = slice.pct >= 25 ? 15 : 11;
    const needed   = String(Math.round(slice.pct) + "%").length * fontSize * 0.62 * 1.25;
    let   lR       = (R + INNER_R) / 2;
    if (lR * sweep < needed) lR = R - fontSize;         // slide out to the wide end
    const showLabel = lR * sweep >= needed && lR > INNER_R + 4;
    const lx = lR * Math.cos(mid);
    const ly = lR * Math.sin(mid);

    const d = arcPath(INNER_R, R, start, end);

    /* ADDED 2026-09-24 — the halo arc. Always HALO_R0..HALO_R1 whatever the
       wedge does, which is what keeps the outside a clean circle. Skipped
       for a sliver too thin to survive the gap on both sides. */
    const halo = sweep > HALO_GAP * 2.5
      ? arcPath(HALO_R0, HALO_R1, start + HALO_GAP, end - HALO_GAP)
      : null;

    return { ...slice, i, d, halo, lx, ly, sweep, fontSize, showLabel };
  });

  const active = hovered !== null && hovered >= 0 && hovered < pieData.length;
  const activeSlice = active ? pieData[hovered] : null;

  return (
    /* CHANGED 2026-09-24: viewBox opened from 204 to 284 wide. The old one
       trimmed 18 units off each edge, which would now clip the outer
       hairline at 137. */
    <svg viewBox={`0 0 ${CX*2} ${CY*2}`} style={{width:"100%", display:"block"}}>
      <defs>
        <filter id="petalShadow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="2.5"/>
          <feOffset dx="0.6" dy="1.4"/>
          <feComponentTransfer><feFuncA type="linear" slope="0.35"/></feComponentTransfer>
          <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* ADDED 2026-09-24 — thin concentric rings.
          Pure decoration: no value is attached to any of them. Drawn first so
          everything else sits on top. Two live in the gap between the pie and
          the halo; two sit just outside the halo. */}
      <g transform={`translate(${CX},${CY})`} style={{pointerEvents:"none"}}>
        {HAIRLINES.map(r => (
          <circle key={"h"+r} cx={0} cy={0} r={r}
            fill="none" stroke="var(--border, #E5E7EB)" strokeWidth={1}/>
        ))}
      </g>

      {/* ADDED 2026-09-24 — the halo ring.
          Each arc is its own wedge's colour at 45%, all on the same radius.
          Outside the shadow filter on purpose — a drop shadow on a 45%
          colour turns it muddy. Dims with its wedge on hover. */}
      <g transform={`translate(${CX},${CY})`} style={{pointerEvents:"none"}}>
        {wedges.filter(w => w.halo).map(w => (
          <path key={"ha"+w.i} d={w.halo} fill={haloTint(w.color)}
            opacity={active && hovered !== w.i ? 0.35 : 1}
            style={{transition:"opacity 0.15s ease"}}/>
        ))}
      </g>

      {/* Wedges (shadow filter wraps all of them).
          CHANGED 2026-09-24: the gap between wedges was a hardcoded #FFFFFF,
          which drew white lines across a dark card in dark mode. It now
          follows the card colour. */}
      <g transform={`translate(${CX},${CY})`} filter="url(#petalShadow)">
        {wedges.map(w => (
          <path key={w.i}
            d={w.d}
            fill={w.color}
            stroke="var(--bg-card, #FFFFFF)" strokeWidth={3} strokeLinejoin="round"
            opacity={active && hovered !== w.i ? 0.45 : 1}
            style={{cursor:"pointer", transition:"opacity 0.15s ease"}}
            onMouseEnter={()=>onHover(w.i)}
            onMouseLeave={()=>onHover(null)}/>
        ))}
      </g>

      {/* % labels (non-interactive, drawn over wedges) */}
      <g transform={`translate(${CX},${CY})`} style={{pointerEvents:"none"}}>
        {/* CHANGED 2026-09-24: the old test was a flat `pct >= 5`. It now
            asks whether the label actually FITS the wedge — see the
            showLabel calculation above. */}
        {wedges.filter(w => w.showLabel).map(w => (
          <text key={"l"+w.i}
            x={w.lx} y={w.ly}
            textAnchor="middle" dy={3}
            fontSize={w.fontSize}
            fontWeight={700}
            fill="#FFFFFF">
            {Math.round(w.pct)}%
          </text>
        ))}
      </g>

      {/* Center circle + dynamic text */}
      <g transform={`translate(${CX},${CY})`} style={{pointerEvents:"none"}}>
        {/* CHANGED 2026-09-24: r 29 -> HUB_R, and the hardcoded #FFFFFF /
            #E5E7EB / #111 became theme variables. In dark mode this disc
            used to render white with black text inside a dark card. */}
        <circle cx={0} cy={0} r={HUB_R}
          fill="var(--bg-card, #FFFFFF)" stroke="var(--border, #E5E7EB)" strokeWidth={1.2}/>
        {activeSlice ? (
          <>
            <text x={0} y={-7} textAnchor="middle" fontSize={10} fontWeight={700}
              fill={activeSlice.color}>
              {activeSlice.name.length > 10 ? activeSlice.name.slice(0,9)+"…" : activeSlice.name}
            </text>
            <text x={0} y={6} textAnchor="middle" fontSize={11} fontWeight={700}
              fill="var(--text-primary, #111827)">
              {activeSlice.rev}
            </text>
            <text x={0} y={19} textAnchor="middle" fontSize={9} fill="var(--text-secondary, #6B7280)">
              {Math.round(activeSlice.pct)}%
            </text>
          </>
        ) : (
          <>
            <text x={0} y={-2} textAnchor="middle" fontSize={13} fontWeight={700}
              fill="var(--text-primary, #111827)">
              {totalRev || "$0"}
            </text>
            <text x={0} y={12} textAnchor="middle" fontSize={6.5}
              fill="var(--text-secondary, #6B7280)" letterSpacing={0.8}>
              THIS MONTH
            </text>
          </>
        )}
      </g>
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────
   Tooltips + StatCard
   ───────────────────────────────────────────────────────────── */
function SparkTip({active,payload,label,unit,metricLabel}){
  if(!active||!payload?.length)return null;
  const cur=payload.find(p=>p.dataKey==="cur")?.value;
  const last=payload.find(p=>p.dataKey==="last")?.value;
  const ord=n=>{const s=["th","st","nd","rd"],v=n%100;return n+(s[(v-20)%10]||s[v]||s[0]);};
  // CHANGED 2026-09-18: a real zero is now shown as "0", and only a MISSING
  // day shows "–". The old version printed "-" for both, which hid the
  // difference between "nothing happened that day" and "that day has not
  // happened yet".
  const fmt=v=>{
    if(v==null)return "–";
    if(unit==="$")return `$${Number(v).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`;
    if(unit==="%")return `${Number(v).toFixed(2)}%`;
    return Number(v).toLocaleString();
  };
  return(
    <div className="stip">
      {/* CHANGED 2026-09-18: says "by the end of" because the value is a
          running total to that day, not that single day's figure. */}
      <div className="stip-t"><i>by end of {ord(label)}</i></div>
      <table className="stip-tbl">
        <thead><tr><th></th><th>CURRENT<br/>MONTH</th><th>LAST<br/>MONTH</th></tr></thead>
        <tbody><tr><td className="stip-m">{metricLabel || (unit==="$"?"Revenue":"Clicks")}</td><td>{fmt(cur)}</td><td className="stip-l">{fmt(last)}</td></tr></tbody>
      </table>
    </div>
  );
}

function PerfTip({active,payload,label,timeLabel,lblA,lblB,selMetrics}){
  if(!active||!payload?.length)return null;
  // Row payload gives us access to all fields on the bucket, including the
  // per-day values (cT_day, rT_day, etc.) which we prefer for tooltips when
  // present. Weekly/monthly buckets carry both cumulative (for the line) and
  // per-day (for the tooltip). Hourly buckets carry both too.
  const row = payload[0]?.payload || {};
  const pick=(base)=>{
    const dayKey = base + "_day";
    if (Object.prototype.hasOwnProperty.call(row, dayKey)) return row[dayKey];
    return row[base];
  };
  const fmtV=(k,v)=>{
    if(v==null||v===undefined)return"-";
    if(k==="rT"||k==="rY")return v>0?"$"+parseFloat(v).toFixed(2):"-";
    if(k==="cvrT"||k==="cvrY")return parseFloat(v).toFixed(2)+"%";
    return v;
  };
  // Per-bucket dates (weekly/monthly): the data point carries dateA / dateB
  // strings like "Mon Apr 20" / "Mon Apr 13", so the tooltip can label the
  // two columns with the actual dates being compared at this bucket.
  const headerA = row.dateA || lblA || "TODAY";
  const headerB = row.dateB || lblB || "YESTERDAY";

  const rows=[
    // CHANGED 2026-09-24: the tooltip's coloured metric names now read from
    // SERIES, so they always match the line drawn on the chart.
    {k1:"cT",k2:"cY",label:"Clicks",   color:SERIES.clicks.line,  show: selMetrics?.includes("cT")},
    {k1:"rT",k2:"rY",label:"Revenue",  color:SERIES.revenue.line, show: selMetrics?.includes("rT")},
    {k1:"cvT",k2:"cvY",label:"CV",     color:SERIES.cv.line,      show: selMetrics?.includes("cv")},
    {k1:"cvrT",k2:"cvrY",label:"CVR",  color:SERIES.cvr.line,     show: selMetrics?.includes("cvr")},
  ].filter(r=>r.show);
  return(
    <div className="ptip">
      <div className="ptip-hd"><i className="ptip-dt">{timeLabel||lblA||"Today"}</i><span className="ptip-tm">{label}</span></div>
      <table className="ptip-tbl">
        <thead><tr><th></th><th>{headerA}</th><th>{headerB}</th></tr></thead>
        <tbody>
          {rows.map(r=>(
            <tr key={r.k1}>
              <td style={{color:r.color,fontWeight:700}}>{r.label}</td>
              <td>{fmtV(r.k1,pick(r.k1))}</td>
              <td className="pt-old">{fmtV(r.k2,pick(r.k2))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * StatCard
 *
 * CHANGED 2026-09-18 — two new props:
 *   exact       — the unrounded value, shown as a browser tooltip on the big
 *                 number. "$1.2K" is convenient but lossy; hovering now
 *                 gives you the real figure.
 *   metricLabel — what the sparkline tooltip should call this metric.
 *
 * Also: when the sparkline has no real data behind it, the card says so
 * instead of drawing an empty chart box. It NEVER draws a made-up line.
 */
function StatCard({title,monthVal,exact,pct,up,today,yesterday,lastMonth,data,unit,metricLabel,badgeNote}){
  const gid="g"+title.replace(/\W/g,"");
  const hasSpark = sparkHasData(data);           // ADDED 2026-09-18
  return(
    <div className="sc">
      <div className="sc-title-row"><span className="sc-title">{title}</span></div>
      <div className="sc-month-row">
        <span className="sc-lbl">Current Month</span>
        {/* ADDED 2026-09-19: title={badgeNote} — hover the percentage to see
            exactly which window it is comparing against, e.g. "vs 1–19 Aug".
            The row below still shows the FULL last month, so without this
            the two could look contradictory. */}
        <span className={"sc-badge "+(up?"b-up":"b-dn")} title={badgeNote || undefined}>
          {up?<svg width="14" height="14" viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill="#16A34A"/><polyline points="7,14 12,9 17,14" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
            :<svg width="14" height="14" viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill="#DC2626"/><polyline points="7,10 12,15 17,10" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          <span className={up?"pct-up":"pct-dn"}>{pct}</span>
        </span>
      </div>
      {/* ADDED 2026-09-18: title={exact} — hover the big number for the
          unrounded value, since "1.2K" hides up to 99 of anything. */}
      <div className="sc-big" title={exact != null ? String(exact) : undefined}>{monthVal}</div>
      <div className="sc-spark">
        {hasSpark ? (
          <ResponsiveContainer width="100%" height={68}>
            <AreaChart data={data} margin={{top:4,right:0,left:0,bottom:4}}>
              <defs>
                {/* CHANGED 2026-09-24: gradient was #2563EB at 0.25 → 0.03,
                    and Recharts then multiplied it by its default 0.6, so it
                    topped out around 0.15 — practically invisible. Now the
                    reference blue, and the <Area> below pins fillOpacity={1}
                    so these numbers are what actually gets painted. */}
                <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={SERIES.clicks.line} stopOpacity={0.48}/>
                  <stop offset="100%" stopColor={SERIES.clicks.line} stopOpacity={0.06}/>
                </linearGradient>
              </defs>
              <Tooltip content={<SparkTip unit={unit} metricLabel={metricLabel}/>} cursor={{stroke:"#374151",strokeWidth:1}}/>
              {/* CHANGED 2026-09-24: last month's band was #DBEAFE at 0.55 —
                  on a white card that is very nearly white. #D5E4F9 is the
                  band colour read straight out of Aditi's reference image. */}
              <Area type="monotone" dataKey="last" stroke="none" fill="#D5E4F9" fillOpacity={1} dot={false} isAnimationActive={false} activeDot={false}/>
              {/* CHANGED 2026-09-24: line #2563EB → the reference #71A6E9, and
                  fillOpacity={1} added so the gradient above isn't re-faded. */}
              <Area type="monotone" dataKey="cur" stroke={SERIES.clicks.line} strokeWidth={2} fill={"url(#"+gid+")"} fillOpacity={1} dot={false} isAnimationActive={false} activeDot={{r:4,fill:"#fff",stroke:SERIES.clicks.line,strokeWidth:2}}/>
              <XAxis dataKey="day" hide={true}/>
              <YAxis hide={true} domain={["dataMin","dataMax"]}/>
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          // ADDED 2026-09-18: no invented line when there is nothing to draw.
          <div style={{height:68,display:"flex",alignItems:"center",justifyContent:"center",
                       fontSize:11,color:"var(--text-muted)"}}>
            No daily data
          </div>
        )}
      </div>
      <div className="sc-rows">
        <div className="sc-row"><span className="sc-rl">Today</span><span className="sc-rv">{today}</span></div>
        <div className="sc-row"><span className="sc-rl">Yesterday</span><span className="sc-rv">{yesterday}</span></div>
        <div className="sc-row sc-last"><span className="sc-rl">Last Month</span><span className="sc-rv">{lastMonth}</span></div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Main Dashboard component
   ───────────────────────────────────────────────────────────── */
export default function Dashboard(){
  const [metrics,setMetrics]=useState(["cT","rT"]);
  const [time,setTime]=useState("ty");
  const [mOpen,setMOpen]=useState(false);
  const [tOpen,setTOpen]=useState(false);
  const [pieHov,setPieHov]=useState(null);
  const [ctype,setCtype]=useState("line");

  // ── Real API data ──
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);   // ADDED 2026-09-18
  const [realPerf,     setRealPerf]     = useState(null);
  const [pieChartData, setPieChartData] = useState(null);
  const [pieReady,     setPieReady]     = useState(false);   // ADDED 2026-09-18
  const [totalRevDisplay, setTotalRevDisplay] = useState("$0");
  // CHANGED 2026-09-18: perfLoading is finally USED (it was declared, then
  // silenced with an eslint-disable and never read).
  const [perfLoading,  setPerfLoading]  = useState(false);
  const [perfError,    setPerfError]    = useState(null);    // ADDED 2026-09-18
  const [perfFetchedAt,setPerfFetchedAt]= useState(null);    // ADDED 2026-09-18
  const [perfSummary,  setPerfSummary]  = useState(null);
  const [lastSync,     setLastSync]     = useState(null);   // cron's last_run_at
  const [refreshTick,  setRefreshTick]  = useState(0);      // incremented by polling to trigger stat-card re-fetch
  const lastRunRef = useRef(null);  // tracks last seen cron timestamp

  /*  Compare "range A" vs "range B" for the performance chart.
      All date math goes through edt.js — no raw new Date() calendar reads.

      TWO PATHS:
        - Weekly ('ww') and Monthly ('mm'): /api/revenue/chart returns real
          per-day data from the cache. Each bucket = one real day.
        - Hourly ('ty' and 'ts'): /api/revenue/chart/hourly returns real
          per-hour data for EVERY network.

          CHANGED 2026-09-18 — the comment that used to sit here said the
          hourly path "keeps the old HOUR_WEIGHTS approach" and that "the
          backend doesn't expose per-hour data yet (Patch 2 will)". Both
          statements are now false. HOUR_WEIGHTS was deleted from
          routes/revenue.js today and services/cake.js grew a real
          getHourlyByDate() backed by Cake's Reports/HourlySummary. Verified
          against the daily reports: CX3Ads 1018 clicks / $1096, Velko 33 /
          $24, Instar 7 / $57 — hourly sums matched daily exactly.

      `reset` (ADDED 2026-09-18): true when the user changed the Time
      dropdown, false for the silent 5-minute refresh. On a reset we clear
      the old series first, so a failed fetch can never leave last month's
      numbers sitting under a "Today vs Yesterday" label.  */
  const fetchPerfForTime = useCallback(async (timeKey, _metricKeys, reset = false) => {
    setPerfLoading(true);
    if (reset) {                       // ADDED 2026-09-18
      setRealPerf(null);
      setPerfSummary(null);
    }
    setPerfError(null);                // ADDED 2026-09-18
    try {
      const ep = edtParts();

      let rangeA, rangeB, labelA, labelB;
      if      (timeKey==="ty") { rangeA="today";     rangeB="yesterday";     labelA="Today";      labelB="Yesterday";           }
      else if (timeKey==="ts") { rangeA="today";     rangeB="last_same_day"; labelA="Today";      labelB=`Last ${_todayName}`;  }
      else if (timeKey==="ww") { rangeA="this_week"; rangeB="last_week";     labelA="This Week";  labelB="Last Week";           }
      else if (timeKey==="mm") { rangeA="month";     rangeB="last_month";    labelA="This Month"; labelB="Last Month";          }
      else                     { rangeA="today";     rangeB="yesterday";     labelA="Today";      labelB="Yesterday";           }

      // ── Weekly / Monthly: real per-day data from /chart endpoint ──
      if (timeKey === "ww" || timeKey === "mm") {
        const [resA, resB] = await Promise.all([
          axios.get(`${API}/revenue/chart?range=${rangeA}`),
          axios.get(`${API}/revenue/chart?range=${rangeB}`),
        ]);
        const dataA = resA.data?.data || [];
        const dataB = resB.data?.data || [];
        const sumA  = resA.data?.summary || {};
        const sumB  = resB.data?.summary || {};

        // Build chart buckets by aligning day_num (day 0 = Mon for week, day 0 = 1st for month)
        // If one range is longer than the other (e.g. this month has fewer days than last month
        // so far), use the longer one as the bucket spine and fill missing with nulls.
        const N = Math.max(dataA.length, dataB.length);
        const chart = Array.from({length: N}, (_, i) => {
          const a = dataA[i];
          const b = dataB[i];

          // X-axis label: short date like "20" for month, "Mon" for week
          const label = timeKey === "ww"
            ? ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i % 7]
            : a ? a.date.slice(8, 10) : (b ? b.date.slice(8, 10) : String(i+1).padStart(2,"0"));

          // Per-bucket date labels for the tooltip (show actual dates)
          const dateA = a ? ymdToDisplayFull(a.date) : null;
          const dateB = b ? ymdToDisplayFull(b.date) : null;

          // Everflow-style display: the chart LINE shows cumulative running
          // totals (always rising, smooth curve), matching how Everflow's
          // reporting charts look. The tooltip still shows the per-day value
          // so hovering gives meaningful day-by-day context.
          //
          // cT/cY/rT/rY/cvT/cvY  → cumulative (drives the line)
          // cT_day, cY_day, etc. → per-day (used in tooltip)
          const aClicksDay = a ? a.clicks      : null;
          const aRevDay    = a ? a.revenue     : null;
          const aConvDay   = a ? a.conversions : null;
          const bClicksDay = b ? b.clicks      : 0;
          const bRevDay    = b ? b.revenue     : 0;
          const bConvDay   = b ? b.conversions : 0;

          const aClicksCum = a ? a.clicks_cum      : null;
          const aRevCum    = a ? a.revenue_cum     : null;
          const aConvCum   = a ? a.conversions_cum : null;
          const bClicksCum = b ? b.clicks_cum      : 0;
          const bRevCum    = b ? b.revenue_cum     : 0;
          const bConvCum   = b ? b.conversions_cum : 0;

          // CVR is always "overall-to-date" (cumulative conversions / cumulative clicks)
          const cvrA = (aClicksCum && aClicksCum > 0) ? (aConvCum / aClicksCum * 100) : null;
          const cvrB = (bClicksCum > 0)               ? (bConvCum / bClicksCum * 100) : 0;

          return {
            time:  label,
            dateA, dateB,
            // Cumulative values power the chart line
            cT:    aClicksCum,
            cY:    bClicksCum,
            rT:    aRevCum,
            rY:    bRevCum,
            cvT:   aConvCum,
            cvY:   bConvCum,
            cvrT:  cvrA != null ? parseFloat(cvrA.toFixed(2)) : null,
            cvrY:  parseFloat(cvrB.toFixed(2)),
            // Per-day values for the tooltip
            cT_day: aClicksDay,
            cY_day: bClicksDay,
            rT_day: aRevDay,
            rY_day: bRevDay,
            cvT_day: aConvDay,
            cvY_day: bConvDay,
          };
        });

        setRealPerf(chart);
        setPerfFetchedAt(new Date());          // ADDED 2026-09-18
        setPerfSummary({
          labelA, labelB,
          clicks:  { a: parseInt  (sumA.total_clicks      || 0), b: parseInt  (sumB.total_clicks      || 0) },
          revenue: { a: parseFloat(sumA.total_revenue     || 0), b: parseFloat(sumB.total_revenue     || 0) },
          conv:    { a: parseInt  (sumA.total_conversions || 0), b: parseInt  (sumB.total_conversions || 0) },
          cvr: {
            a: (sumA.total_clicks > 0) ? (sumA.total_conversions / sumA.total_clicks * 100) : 0,
            b: (sumB.total_clicks > 0) ? (sumB.total_conversions / sumB.total_clicks * 100) : 0,
          },
        });
        return;
      }

      // ── Hourly ('ty', 'ts'): real per-hour data from /chart/hourly ──
      // Every network — Everflow AND Cake — now returns genuine per-hour
      // numbers. Nothing on this path is distributed, weighted or smoothed
      // before it reaches the chart.
      const todayStr = todayYMD();
      const dateAStr = todayStr;
      const dateBStr = timeKey === "ty" ? yesterdayYMD() : addDaysYMD(todayStr, -7);

      const [resA, resB] = await Promise.all([
        axios.get(`${API}/revenue/chart/hourly?date=${dateAStr}`),
        axios.get(`${API}/revenue/chart/hourly?date=${dateBStr}`),
      ]);
      const hoursA = resA.data?.data || [];
      const hoursB = resB.data?.data || [];
      const sumA   = resA.data?.summary || {};
      const sumB   = resB.data?.summary || {};

      // ADDED 2026-09-18: the backend now reports which networks could not
      // answer. If any did, say so above the chart rather than letting the
      // total quietly read low.
      const skippedA = resA.data?.skipped || [];
      const skippedB = resB.data?.skipped || [];

      const limitA = ep.hour;  // current EDT hour — stop today's line here

      // Build 24 cumulative points matching the Everflow look: the chart
      // LINE shows running totals (always rising), tooltip shows per-hour.
      let cumA = { c: 0, r: 0, cv: 0 };
      let cumB = { c: 0, r: 0, cv: 0 };
      const chart = Array.from({ length: 24 }, (_, i) => {
        const label = String(i).padStart(2, "0") + ":00";
        const hB = hoursB[i] || { clicks: 0, revenue: 0, conversions: 0 };
        cumB.c  += hB.clicks || 0;
        cumB.r  += hB.revenue || 0;
        cumB.cv += hB.conversions || 0;

        const row = {
          time: label,
          // Yesterday / comparison period (full 24 hrs)
          cY: cumB.c,
          rY: parseFloat(cumB.r.toFixed(2)),
          cvY: cumB.cv,
          cvrY: cumB.c > 0 ? parseFloat(((cumB.cv / cumB.c) * 100).toFixed(2)) : 0,
          // Per-hour values for tooltip display
          cY_day: hB.clicks || 0,
          rY_day: parseFloat((hB.revenue || 0).toFixed(2)),
          cvY_day: hB.conversions || 0,
        };

        if (i <= limitA) {
          const hA = hoursA[i] || { clicks: 0, revenue: 0, conversions: 0 };
          cumA.c  += hA.clicks || 0;
          cumA.r  += hA.revenue || 0;
          cumA.cv += hA.conversions || 0;
          row.cT = cumA.c;
          row.rT = parseFloat(cumA.r.toFixed(2));
          row.cvT = cumA.cv;
          row.cvrT = cumA.c > 0 ? parseFloat(((cumA.cv / cumA.c) * 100).toFixed(2)) : 0;
          row.cT_day  = hA.clicks || 0;
          row.rT_day  = parseFloat((hA.revenue || 0).toFixed(2));
          row.cvT_day = hA.conversions || 0;
        } else {
          // Hours that have not happened yet: undefined, not zero, so the
          // line stops rather than running flat along the bottom.
          row.cT = undefined;
          row.rT = undefined;
          row.cvT = undefined;
          row.cvrT = undefined;
          row.cT_day = undefined;
          row.rT_day = undefined;
          row.cvT_day = undefined;
        }
        return row;
      });

      const totA = {
        clicks:  parseInt(sumA.total_clicks      || 0),
        revenue: parseFloat(sumA.total_revenue   || 0),
        conv:    parseInt(sumA.total_conversions || 0),
      };
      const totB = {
        clicks:  parseInt(sumB.total_clicks      || 0),
        revenue: parseFloat(sumB.total_revenue   || 0),
        conv:    parseInt(sumB.total_conversions || 0),
      };

      setRealPerf(chart);
      setPerfFetchedAt(new Date());          // ADDED 2026-09-18
      setPerfSummary({
        labelA, labelB,
        clicks:  { a: totA.clicks,  b: totB.clicks  },
        revenue: { a: totA.revenue, b: totB.revenue },
        conv:    { a: totA.conv,    b: totB.conv    },
        cvr: {
          a: totA.clicks > 0 ? (totA.conv / totA.clicks * 100) : 0,
          b: totB.clicks > 0 ? (totB.conv / totB.clicks * 100) : 0,
        },
        // ADDED 2026-09-18: names of networks that returned nothing.
        skipped: [...new Set([...skippedA, ...skippedB].map(s => s.affiliate))],
      });
    } catch(e) {
      // ── CHANGED 2026-09-18 ────────────────────────────────────────────
      // This used to be `catch(e) { console.error("PerfChart error:", e); }`
      // and nothing else. Because the chart fell back to the random
      // perfData series, a failure here put an invented chart on screen and
      // left it there — looking completely normal. Now the failure is
      // recorded and shown.
      console.error("PerfChart error:", e);
      setPerfError(e.response?.data?.message || e.message || "Request failed");
    }
    finally { setPerfLoading(false); }
  }, []);

  /*  Main dashboard load — stat cards, sparklines and pie.
      Every date sent to the backend is an EDT YMD from edt.js.
      Re-runs when refreshTick increments (triggered by smart polling
      when the cron's last_run_at timestamp changes). */
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setStatsError(null);                      // ADDED 2026-09-18
        const ep = edtParts();

        // ADDED 2026-09-19: the matching span of last month, for the badge.
        const lmSpan = lastMonthSameSpan(ep);     // e.g. 2026-08-01 → 2026-08-19

        // CHANGED 2026-09-19: seven requests.
        //   • 4 summary pulls (today / yesterday / this month / last month)
        //   • 2 per-day pulls that feed the real sparklines  (added 09-18)
        //   • 1 NEW pull: the SAME SPAN of last month, so the percentage
        //     badge compares 19 days against 19 days instead of 19 against
        //     31. All seven are cache reads, and they run in parallel.
        const [todayRes, yestRes, monthRes, lastMonthRes, monthDaysRes, lastMonthDaysRes, lmSpanRes] = await Promise.all([
          axios.get(`${API}/revenue?range=today&groupBy=offer&cached_only=true`),
          axios.get(`${API}/revenue?range=yesterday&groupBy=offer&cached_only=true`),
          axios.get(`${API}/revenue?range=month&groupBy=offer&cached_only=true`),
          axios.get(`${API}/revenue?range=last_month&groupBy=offer&cached_only=true`),
          axios.get(`${API}/revenue/chart?range=month`),
          axios.get(`${API}/revenue/chart?range=last_month`),
          axios.get(`${API}/revenue?range=custom&start=${lmSpan.start}&end=${lmSpan.end}&groupBy=offer&cached_only=true`),
        ]);

        const sum = res => res.data?.summary || {};
        const todayS     = sum(todayRes);
        const yestS      = sum(yestRes);
        const monthS     = sum(monthRes);
        const lastMonthS = sum(lastMonthRes);
        // ADDED 2026-09-19: last month, SAME number of days as this month so
        // far. Used ONLY by the percentage badges. The "Last Month" row on
        // each card still reads from lastMonthS (the full month).
        const lmSpanS    = sum(lmSpanRes);

        // Real per-day rows for the sparklines.
        const monthDays     = monthDaysRes.data?.data     || [];
        const lastMonthDays = lastMonthDaysRes.data?.data || [];

        const fmtRev = v => {
          const n = parseFloat(v||0);
          if (n>=1000) return `$${(n/1000).toFixed(1)}K`;
          return `$${n.toFixed(2)}`;
        };
        const fmtNum = v => {
          const n = parseInt(v||0);
          if (n>=1000) return `${(n/1000).toFixed(1)}K`;
          return String(n);
        };
        const fmtPct = (part, whole) => {
          if (!whole || whole===0) return "0.00%";
          return `${((part/whole)*100).toFixed(2)}%`;
        };
        const pctChange = (now, prev) => {
          if (!prev || prev===0) return { pct:"0%", up:true };
          const ch = ((now-prev)/prev*100).toFixed(0);
          return { pct:`${ch>0?"+":""}${ch}%`, up: ch>=0 };
        };

        const mClicks  = parseInt  (monthS.total_clicks      || 0);
        const mRev     = parseFloat(monthS.total_revenue     || 0);
        const mConv    = parseInt  (monthS.total_conversions || 0);
        const mEvents  = parseInt  (monthS.total_events      || 0);   // ADDED 2026-09-18
        // FULL last month — powers the "Last Month" row at the bottom of
        // each card. Unchanged.
        const lmClicks = parseInt  (lastMonthS.total_clicks      || 0);
        const lmRev    = parseFloat(lastMonthS.total_revenue     || 0);
        const lmConv   = parseInt  (lastMonthS.total_conversions || 0);
        const lmEvents = parseInt  (lastMonthS.total_events      || 0);   // ADDED 2026-09-18

        // ADDED 2026-09-19 — SAME SPAN of last month (1st to the same day
        // number). These, and only these, drive the percentage badges.
        const spClicks = parseInt  (lmSpanS.total_clicks      || 0);
        const spRev    = parseFloat(lmSpanS.total_revenue     || 0);
        const spConv   = parseInt  (lmSpanS.total_conversions || 0);
        const spEvents = parseInt  (lmSpanS.total_events      || 0);

        const tClicks  = parseInt  (todayS.total_clicks      || 0);
        const tRev     = parseFloat(todayS.total_revenue     || 0);
        const tConv    = parseInt  (todayS.total_conversions || 0);
        const tEvents  = parseInt  (todayS.total_events      || 0);   // ADDED 2026-09-18
        const yClicks  = parseInt  (yestS.total_clicks       || 0);
        const yRev     = parseFloat(yestS.total_revenue      || 0);
        const yConv    = parseInt  (yestS.total_conversions  || 0);
        const yEvents  = parseInt  (yestS.total_events       || 0);   // ADDED 2026-09-18

        const mCvr  = mClicks  > 0 ? (mConv  / mClicks  * 100) : 0;
        const mEvr  = mClicks  > 0 ? (mEvents / mClicks  * 100) : 0;

        // ── FIXED 2026-09-18, then CHANGED 2026-09-19 ───────────────────
        // 09-18 fixed a divide-by-zero: the old line was
        //     pctChange(mCvr, mClicks>0 ? (lmConv/lmClicks*100) : 0)
        // which guarded on THIS month's clicks but divided by LAST month's,
        // rendering "NaN%" whenever last month had none.
        // 09-19 changed WHICH last-month figures these use: the same span
        // (1st→same day) rather than the whole month, so the ratios are
        // comparable. Rates like CVR and EVR are less distorted by day count
        // than totals are, but they still move over a month, so the matching
        // span is the honest baseline for them too.
        const spCvr = spClicks > 0 ? (spConv   / spClicks * 100) : 0;
        const spEvr = spClicks > 0 ? (spEvents / spClicks * 100) : 0;
        // (The "Last Month" ROW computes its own CVR/EVR inline from the
        //  full-month totals, via fmtPct(lmConv, lmClicks) below.)

        // Pie chart — grouped by affiliate, from monthly data
        const monthRows = monthRes.data?.data || [];
        const affRevMap = {};
        monthRows.forEach(r => {
          const name = r.affiliate_name || "Unknown";
          affRevMap[name] = (affRevMap[name]||0) + parseFloat(r.revenue||0);
        });
        const totalRevForPie = Object.values(affRevMap).reduce((a,b)=>a+b,0) || 1;
        const pieArr = Object.entries(affRevMap)
          .sort((a,b)=>b[1]-a[1])
          .slice(0, 8)
          .map(([name, rev], i) => ({
            name,
            pct: (rev/totalRevForPie*100),
            rev: rev>=1000 ? "$"+(rev/1000).toFixed(1)+"K" : "$"+rev.toFixed(0),
            color: PIE_COLORS[i % PIE_COLORS.length],
          }));
        // ── FIXED 2026-09-18 ────────────────────────────────────────────
        // Was: if (pieArr.length) { setPieChartData(pieArr); ... }
        // A month with no revenue produced an empty array, the state was
        // never set, and the pie sat on "Loading affiliate data..." forever.
        // Now it is always set, and pieReady tells the component the fetch
        // finished so it can say "No revenue recorded this month." instead.
        setPieChartData(pieArr);
        setTotalRevDisplay(fmtRev(Object.values(affRevMap).reduce((a,b)=>a+b,0)));
        setPieReady(true);

        setStats({
          // CHANGED 2026-09-19: every `pctChange` below now measures against
          // the SAME SPAN of last month (sp*) instead of the whole month
          // (lm*). `badgeNote` is the hover text that names the window.
          // The `lastMonth:` lines are untouched — still the full month.
          badgeNote: lmSpan.label,                      // e.g. "vs 1–19 Aug"
          clicks:  {
            monthVal: fmtNum(mClicks), exact: mClicks,
            ...pctChange(mClicks, spClicks),
            today:     fmtNum(tClicks),
            yesterday: fmtNum(yClicks),
            lastMonth: fmtNum(lmClicks),
            // CHANGED 2026-09-18: real per-day series, not mkSpark's formula.
            spark: buildSpark(monthDays, lastMonthDays, PICK_CLICKS),
          },
          revenue: {
            monthVal: fmtRev(mRev), exact: mRev.toFixed(2),
            ...pctChange(mRev, spRev),
            today:     fmtRev(tRev),
            yesterday: fmtRev(yRev),
            lastMonth: fmtRev(lmRev),
            spark: buildSpark(monthDays, lastMonthDays, PICK_REV),
          },
          conv: {
            monthVal: String(mConv), exact: mConv,
            ...pctChange(mConv, spConv),
            today:     String(tConv),
            yesterday: String(yConv),
            lastMonth: String(lmConv),
            spark: buildSpark(monthDays, lastMonthDays, PICK_CONV),
          },
          cvr: {
            monthVal: fmtPct(mConv, mClicks), exact: mCvr.toFixed(4) + "%",
            ...pctChange(mCvr, spCvr),
            today:     fmtPct(tConv, tClicks),
            yesterday: fmtPct(yConv, yClicks),
            lastMonth: fmtPct(lmConv, lmClicks),
            spark: buildSpark(monthDays, lastMonthDays, PICK_CVR),
          },
          // ── FIXED 2026-09-18 ──────────────────────────────────────────
          // Events and EVR were hardcoded in the JSX below:
          //   <StatCard title="Events" monthVal="0" pct="0%" up={true}
          //             today="0" yesterday="0" lastMonth="0"
          //             data={mkSpark(0, 0)}/>
          // Every one of those values was typed by hand. revenue_cache has
          // always had an `events` column and both services write to it —
          // no query read it back until today. These now come from
          // SUM(events). If they read 0, that is a measurement: the
          // networks are reporting zero events.
          events: {
            monthVal: fmtNum(mEvents), exact: mEvents,
            ...pctChange(mEvents, spEvents),
            today:     fmtNum(tEvents),
            yesterday: fmtNum(yEvents),
            lastMonth: fmtNum(lmEvents),
            spark: buildSpark(monthDays, lastMonthDays, PICK_EVENTS),
          },
          evr: {
            monthVal: fmtPct(mEvents, mClicks), exact: mEvr.toFixed(4) + "%",
            ...pctChange(mEvr, spEvr),
            today:     fmtPct(tEvents, tClicks),
            yesterday: fmtPct(yEvents, yClicks),
            lastMonth: fmtPct(lmEvents, lmClicks),
            spark: buildSpark(monthDays, lastMonthDays, PICK_EVR),
          },
          totalRevMonth: mRev,
          _day: ep.day,
        });

      } catch(err) {
        // CHANGED 2026-09-18: a failure is now visible on the page, not only
        // in the browser console. `stats` is deliberately NOT cleared — if we
        // already have good numbers, keeping them and flagging the stale
        // refresh beats blanking the page.
        console.error("Dashboard fetch error:", err.message);
        setStatsError(err.response?.data?.message || err.message || "Request failed");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
    const interval = setInterval(fetchAll, 5*60*1000);
    return () => clearInterval(interval);
  }, [refreshTick]);

  // Smart-refresh: poll /sync/last-run every 15 sec.
  // Re-fetch stat cards ONLY when the cron's last_run_at changes.
  // (The Authorization header is set globally by AuthContext on login, so
  // these calls are authenticated even though no headers are passed here.)
  useEffect(() => {
    const POLL_MS = 15 * 1000;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/revenue/sync/last-run`);
        const ts = res.data?.jobs?.["today-sync"]?.last_run_at;
        if (ts && ts !== lastRunRef.current) {
          lastRunRef.current = ts;
          setLastSync(new Date(ts));
          setRefreshTick(t => t + 1);  // triggers main useEffect to re-fetch
        }
      } catch (e) { /* polling failures are non-fatal */ }
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CHANGED 2026-09-18: the first call passes reset=true (the user changed
  // the Time selection, so the old series must go), while the 5-minute
  // interval passes reset=false so a background refresh never blanks a
  // working chart.
  useEffect(() => {
    fetchPerfForTime(time, metrics, true);
    const id = setInterval(() => fetchPerfForTime(time, metrics, false), 5*60*1000);
    return () => clearInterval(id);
  }, [time, metrics, fetchPerfForTime]);

  const toggleM=k=>{if(metrics.includes(k)){if(metrics.length>1)setMetrics(metrics.filter(x=>x!==k));}else{if(metrics.length<2)setMetrics([...metrics,k]);}};

  // Cumulative lines rise smoothly, so monotone looks right for every view.
  const curveType = "monotone";

  // ADDED 2026-09-18: one place that decides what the chart area shows.
  //   "chart"   — we have real data with at least one bucket
  //   "empty"   — the fetch succeeded but returned no buckets
  //   "error"   — the fetch failed and we have nothing to fall back on
  //   "loading" — nothing fetched yet
  // There is deliberately no state in which a chart is drawn from data we
  // did not fetch. `realPerf.length` is checked as well as `realPerf`,
  // because an empty array is truthy and would otherwise render a chart
  // frame with no content in it.
  const chartState = (realPerf && realPerf.length) ? "chart"
                   : perfError                     ? "error"
                   : realPerf                      ? "empty"
                   :                                 "loading";

  return(
    <div className="layout">
      <Sidebar active="dashboard"/>
      <div className="layout-main">
        <Header title="My Stats" subtitle={lastSync ? `Last synced: ${edtTime(lastSync)} EDT` : null}/>
        <div className="content">

          {/* ADDED 2026-09-18: stat-card refresh failures are visible now. */}
          {statsError && (
            <div style={{margin:"0 0 12px",padding:"9px 14px",borderRadius:8,
                         background:"#FEF2F2",border:"1px solid #FECACA",
                         color:"#B91C1C",fontSize:12.5}}>
              ⚠ Couldn't refresh the stat cards — {statsError}.
              {stats ? " Showing the last figures that loaded successfully." : ""}
            </div>
          )}

          {/* ── Stat Cards ── */}
          <div className="scards">
            {loading ? (
              <div style={{gridColumn:"1/-1",textAlign:"center",padding:"32px",color:"var(--text-muted)",fontSize:13}}>
                Loading real data...
              </div>
            ) : stats ? (<>
              {/* ADDED 2026-09-19: badgeNote — the hover text on each
                  percentage, naming the exact window it compares against
                  (e.g. "vs 1–19 Aug"). Same value on all six. */}
              <StatCard title="Clicks"      metricLabel="Clicks"      badgeNote={stats.badgeNote} monthVal={stats.clicks.monthVal}  exact={stats.clicks.exact}  pct={stats.clicks.pct}  up={stats.clicks.up}  today={stats.clicks.today}  yesterday={stats.clicks.yesterday}  lastMonth={stats.clicks.lastMonth}  data={stats.clicks.spark}  unit=""/>
              <StatCard title="Revenue"     metricLabel="Revenue"     badgeNote={stats.badgeNote} monthVal={stats.revenue.monthVal} exact={stats.revenue.exact} pct={stats.revenue.pct} up={stats.revenue.up} today={stats.revenue.today} yesterday={stats.revenue.yesterday} lastMonth={stats.revenue.lastMonth} data={stats.revenue.spark} unit="$"/>
              <StatCard title="Conversions" metricLabel="Conversions" badgeNote={stats.badgeNote} monthVal={stats.conv.monthVal}    exact={stats.conv.exact}    pct={stats.conv.pct}    up={stats.conv.up}    today={stats.conv.today}    yesterday={stats.conv.yesterday}    lastMonth={stats.conv.lastMonth}    data={stats.conv.spark}    unit=""/>
              <StatCard title="CVR"         metricLabel="CVR"         badgeNote={stats.badgeNote} monthVal={stats.cvr.monthVal}     exact={stats.cvr.exact}     pct={stats.cvr.pct}     up={stats.cvr.up}     today={stats.cvr.today}     yesterday={stats.cvr.yesterday}     lastMonth={stats.cvr.lastMonth}     data={stats.cvr.spark}     unit="%"/>
              {/* CHANGED 2026-09-18: these two were hardcoded zeros. Real now. */}
              <StatCard title="Events"      metricLabel="Events"      badgeNote={stats.badgeNote} monthVal={stats.events.monthVal}  exact={stats.events.exact}  pct={stats.events.pct}  up={stats.events.up}  today={stats.events.today}  yesterday={stats.events.yesterday}  lastMonth={stats.events.lastMonth}  data={stats.events.spark}  unit=""/>
              <StatCard title="EVR"         metricLabel="EVR"         badgeNote={stats.badgeNote} monthVal={stats.evr.monthVal}     exact={stats.evr.exact}     pct={stats.evr.pct}     up={stats.evr.up}     today={stats.evr.today}     yesterday={stats.evr.yesterday}     lastMonth={stats.evr.lastMonth}     data={stats.evr.spark}     unit="%"/>
            </>) : (
              // ADDED 2026-09-18: previously rendered `null` — a silent blank
              // row with no explanation.
              <div style={{gridColumn:"1/-1",textAlign:"center",padding:"32px",color:"var(--text-muted)",fontSize:13}}>
                No data could be loaded.
              </div>
            )}
          </div>

          {/* ── Bottom Row: Pie (LEFT) + Performance (RIGHT) ── */}
          <div className="bottom-row">

            {/* PIE CARD */}
            <div className="pie-card">
              <div className="pie-ttl">Revenue Split</div>
              <div className="pie-sub">By Affiliate</div>
              <div className="pie-layered-wrap">
                <LayeredPie hovered={pieHov} onHover={setPieHov}
                  pieData={pieChartData} totalRev={totalRevDisplay} pieReady={pieReady}/>
              </div>
              <div className="pie-legend">
                {(pieChartData || []).map((d, i) => (
                  <div key={i}
                    className={"pie-row "+(pieHov===i ? "pi-on" : "")}
                    onMouseEnter={()=>setPieHov(i)}
                    onMouseLeave={()=>setPieHov(null)}>
                    <span className="pi-dot" style={{background:d.color}}></span>
                    <span className="pi-nm">{d.name}</span>
                    <span className="pi-rv">{d.rev}</span>
                    <span className="pi-pct">{Math.round(d.pct)}%</span>
                  </div>
                ))}
                {pieReady && (!pieChartData || !pieChartData.length) && (
                  <div style={{fontSize:12,color:"var(--text-muted)",padding:"10px 6px"}}>
                    No revenue yet this month.
                  </div>
                )}
              </div>
            </div>

            {/* PERFORMANCE CARD */}
            <div className="perf-card">
              <div className="perf-title">
                Performance
                {/* ADDED 2026-09-18: a quiet spinner label while refreshing an
                    already-drawn chart, so a slow refresh isn't invisible. */}
                {perfLoading && realPerf && (
                  <span style={{marginLeft:8,fontSize:11,fontWeight:500,color:"var(--text-muted)"}}>
                    refreshing…
                  </span>
                )}
              </div>
              <div className="perf-ctrl">
                <div className="ctrl-g">
                  <span className="ctrl-lbl">Metrics</span>
                  <div className="dd-wrap">
                    <button className="ctrl-btn" onClick={()=>{setMOpen(!mOpen);setTOpen(false);}}>
                      {ALL_METRICS.filter(m=>metrics.includes(m.k)).map(m=>m.l).join(", ")} <span className="ctrl-cnt">{metrics.length}/2</span> ▾
                    </button>
                    {mOpen&&<><div className="dd-ov" onClick={()=>setMOpen(false)}/><div className="dd-box">
                      <div className="dd-info">ℹ Select up to 2 items <button className="dd-clear" onClick={()=>setMetrics(["cT"])}>Clear</button></div>
                      {ALL_METRICS.map(m=>(
                        <div key={m.k} className={"dd-item "+(metrics.includes(m.k)?"sel":"")} onClick={()=>toggleM(m.k)}>
                          <span className="dd-chk">{metrics.includes(m.k)?"✓":""}</span>{m.l}
                        </div>
                      ))}
                    </div></>}
                  </div>
                </div>
                <div className="ctrl-g">
                  <span className="ctrl-lbl">Time</span>
                  <div className="dd-wrap">
                    <button className="ctrl-btn" onClick={()=>{setTOpen(!tOpen);setMOpen(false);}}>
                      {TIME_OPTS.find(t=>t.k===time)?.l} ▾
                    </button>
                    {tOpen&&<><div className="dd-ov" onClick={()=>setTOpen(false)}/><div className="dd-box">
                      {TIME_OPTS.map(t=>(
                        <div key={t.k} className={"dd-item "+(time===t.k?"sel":"")} onClick={()=>{setTime(t.k);setTOpen(false);}}>
                          <span className="dd-chk">{time===t.k?"✓":""}</span>{t.l}
                        </div>
                      ))}
                    </div></>}
                  </div>
                </div>
                <div className="ctype-g">
                  <button className={"ctype-btn "+(ctype==="line"?"on":"")} onClick={()=>setCtype("line")}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                  </button>
                  <button className={"ctype-btn "+(ctype==="bar"?"on":"")} onClick={()=>setCtype("bar")}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                  </button>
                </div>
              </div>

              {/* ADDED 2026-09-18: a stale-refresh strip. The chart below is
                  real data from an earlier successful fetch; this says so
                  rather than letting old numbers pass as current. */}
              {perfError && realPerf && (
                <div style={{margin:"0 18px 6px",padding:"7px 12px",borderRadius:7,
                             background:"#FFFBEB",border:"1px solid #FDE68A",
                             color:"#92400E",fontSize:11.5}}>
                  ⚠ Last refresh failed ({perfError}). Showing data from{" "}
                  {perfFetchedAt ? edtTime(perfFetchedAt) + " EDT" : "the previous load"}.
                </div>
              )}

              <div className="chart-area">
                {/* ═══════════════════════════════════════════════════════
                    CHANGED 2026-09-18 — the heart of this file's fix.

                    WAS:   data={realPerf || perfData}
                    where perfData came from buildPerf():
                        cY += Math.round(15 + i*8 + Math.random()*20);

                    So: a random chart on every page load until the fetch
                    returned, and a random chart FOREVER if the first fetch
                    failed — with a normal-looking legend and axis, and only
                    a "Loading..." line underneath as a hint. Nobody could
                    tell it from real data.

                    NOW: three explicit states. No chart is ever drawn from
                    data we did not fetch.
                    ═══════════════════════════════════════════════════════ */}
                {chartState === "loading" && (
                  // NOTE: plain text, no spinner element — dashboard.css has
                  // no .loading-spinner rule, and a div styled by a class
                  // that doesn't exist would render as an invisible box.
                  <div style={{height:280,display:"flex",alignItems:"center",
                               justifyContent:"center",
                               color:"var(--text-muted)",fontSize:13}}>
                    Loading chart data…
                  </div>
                )}

                {chartState === "error" && (
                  <div style={{height:280,display:"flex",flexDirection:"column",
                               alignItems:"center",justifyContent:"center",gap:10,
                               color:"var(--text-muted)",fontSize:13,textAlign:"center",padding:"0 20px"}}>
                    <div style={{fontSize:22}}>⚠</div>
                    <div style={{color:"#B91C1C",fontWeight:600}}>Couldn't load chart data</div>
                    <div style={{fontSize:12}}>{perfError}</div>
                    <button
                      onClick={()=>fetchPerfForTime(time, metrics, true)}
                      style={{marginTop:4,padding:"6px 16px",borderRadius:6,
                              border:"1px solid var(--border-color, #E5E7EB)",
                              background:"#fff",cursor:"pointer",fontSize:12,fontWeight:600}}>
                      Retry
                    </button>
                  </div>
                )}

                {chartState === "empty" && (
                  // ADDED 2026-09-18: the request worked and came back with
                  // nothing. That is a real answer, and different from a
                  // failure — so it gets its own message.
                  <div style={{height:280,display:"flex",alignItems:"center",
                               justifyContent:"center",
                               color:"var(--text-muted)",fontSize:13}}>
                    No data for this period.
                  </div>
                )}

                {chartState === "chart" && (
                  <ResponsiveContainer width="100%" height={280}>
                    {ctype==="bar" ? (
                      <BarChart data={realPerf} margin={{top:12,right:58,left:10,bottom:0}}>
                        <CartesianGrid strokeDasharray="0" stroke="#F0F1F3" vertical={false}/>
                        <XAxis dataKey="time" tick={{fontSize:11,fill:"#9CA3AF"}} tickLine={false} interval={3} axisLine={{stroke:"#E5E7EB"}}/>
                        {/* CHANGED 2026-09-24: axis numbers now take their
                            colour from SERIES, so they match their series. */}
                        <YAxis yAxisId="l" tick={{fontSize:11,fill:AXIS_INK}} tickLine={false} axisLine={false} domain={[0,"auto"]} width={42}/>
                        <YAxis yAxisId="r" orientation="right" tick={{fontSize:11,fill:AXIS_INK}} tickLine={false} axisLine={false} domain={[0,"auto"]} width={42}/>
                        <Tooltip content={<PerfTip timeLabel={perfSummary?.labelA} lblA={perfSummary?.labelA||"Today"} lblB={perfSummary?.labelB||"Yesterday"} selMetrics={metrics}/>}/>
                        {/* ── FIXED 2026-09-18 ────────────────────────────
                            These four Bars used to render unconditionally,
                            so choosing "Clicks" only and switching to the
                            bar view still drew revenue bars. Now gated on
                            `metrics`, exactly like the line view. ── */}
                        {/* CHANGED 2026-09-24: every fill now comes from
                            SERIES. The "yesterday" bars used four hand-typed
                            colours that did not match their own bands in the
                            line view — switching between line and bar changed
                            the colours. They are the same values now. */}
                        {metrics.includes("cT")&&<Bar yAxisId="l" dataKey="cY" fill={SERIES.clicks.band} fillOpacity={1} radius={[2,2,0,0]}/>}
                        {metrics.includes("cT")&&<Bar yAxisId="l" dataKey="cT" fill={SERIES.clicks.line} fillOpacity={1} radius={[2,2,0,0]}/>}
                        {metrics.includes("rT")&&<Bar yAxisId="r" dataKey="rY" fill={SERIES.revenue.band} fillOpacity={1} radius={[2,2,0,0]}/>}
                        {metrics.includes("rT")&&<Bar yAxisId="r" dataKey="rT" fill={SERIES.revenue.line} fillOpacity={1} radius={[2,2,0,0]}/>}
                        {metrics.includes("cv")&&<Bar yAxisId="r" dataKey="cvY" fill={SERIES.cv.band} fillOpacity={1} radius={[2,2,0,0]}/>}
                        {metrics.includes("cv")&&<Bar yAxisId="r" dataKey="cvT" fill={SERIES.cv.line} fillOpacity={1} radius={[2,2,0,0]}/>}
                        {metrics.includes("cvr")&&<Bar yAxisId="r" dataKey="cvrY" fill={SERIES.cvr.band} fillOpacity={1} radius={[2,2,0,0]}/>}
                        {metrics.includes("cvr")&&<Bar yAxisId="r" dataKey="cvrT" fill={SERIES.cvr.line} fillOpacity={1} radius={[2,2,0,0]}/>}
                      </BarChart>
                    ) : (
                      <ComposedChart data={realPerf} margin={{top:12,right:58,left:10,bottom:0}}>
                        <CartesianGrid strokeDasharray="0" stroke="#F0F1F3" vertical={false}/>
                        <XAxis dataKey="time" tick={{fontSize:11,fill:"#9CA3AF"}} tickLine={false} interval={(time==="ww")?0:(time==="mm")?1:3} axisLine={{stroke:"#E5E7EB"}}/>
                        {/* CHANGED 2026-09-24: axis numbers and the rotated
                            "Clicks" / "Revenue" labels now read from SERIES. */}
                        <YAxis yAxisId="l" tick={{fontSize:11,fill:AXIS_INK}} tickLine={false} axisLine={false} domain={[0,"auto"]} width={42}
                          label={{value:"Clicks",angle:-90,position:"insideLeft",dx:-4,style:{fontSize:9,fill:SERIES.clicks.line,fontWeight:700}}}/>
                        <YAxis yAxisId="r" orientation="right" tick={{fontSize:11,fill:AXIS_INK}} tickLine={false} axisLine={false} domain={[0,"auto"]} width={42}
                          label={{value:metrics.includes("cvr")?"CVR":metrics.includes("rT")?"Revenue":"",angle:90,position:"insideRight",dx:4,style:{fontSize:9,fill:metrics.includes("cvr")?SERIES.cvr.line:SERIES.revenue.line,fontWeight:700}}}/>
                        <Tooltip content={<PerfTip timeLabel={perfSummary?.labelA} lblA={perfSummary?.labelA||"Today"} lblB={perfSummary?.labelB||"Yesterday"} selMetrics={metrics}/>}/>
                        {/* ═══ CHANGED 2026-09-24 — TWO FIXES HERE ═══════════
                            (1) Every <Area> now carries fillOpacity={1}.
                                Without it Recharts silently multiplies the
                                alpha in the colour by its own default 0.6,
                                which is the whole reason the bands looked
                                washed out. With it, 0.48 means 0.48.

                            (2) THE GREY OVERLAP IS GONE — this was a paint
                                ORDER problem, not a colour problem.

                                Recharts paints in the order it is given. The
                                old code listed Revenue first and Clicks
                                second, so the blue band was laid ON TOP of
                                the orange one, and blue over orange averages
                                out to a neutral:

                                   orange band on white   → (241,193,162)
                                   blue 48% over that     → (180,180,196)
                                                             R≈G≈B = grey

                                Painted the other way round it stays warm:

                                   blue band on white     → (187,212,244)
                                   orange 46% over that   → (204,170,157)

                                Measured proof: the overlap in Aditi's
                                reference image is #D9C3BF, and clicks-under-
                                revenue computes to #D8C4BE. Her reference
                                paints in the order below; the old code did
                                the opposite.

                            (3) ALL AREAS NOW COME BEFORE ALL LINES.
                                They used to be paired — area, line, area,
                                line — so simply swapping the pairs would have
                                buried the Clicks LINE under the Revenue band.
                                Bands stack among themselves; lines always sit
                                on top of every band. No line can be hidden.
                            ═══════════════════════════════════════════════ */}

                        {/* ── bands, bottom to top: clicks · revenue · cv · cvr ── */}
                        {metrics.includes("cT")&&<Area yAxisId="l" type={curveType} dataKey="cY" stroke="none" fill={SERIES.clicks.band} fillOpacity={1} dot={false} connectNulls activeDot={false}/>}
                        {metrics.includes("rT")&&<Area yAxisId="r" type={curveType} dataKey="rY" stroke="none" fill={SERIES.revenue.band} fillOpacity={1} dot={false} connectNulls activeDot={false}/>}
                        {metrics.includes("cv")&&<Area yAxisId="r" type={curveType} dataKey="cvY" stroke="none" fill={SERIES.cv.band} fillOpacity={1} dot={false} connectNulls activeDot={false}/>}
                        {metrics.includes("cvr")&&<Area yAxisId="r" type={curveType} dataKey="cvrY" stroke="none" fill={SERIES.cvr.band} fillOpacity={1} dot={false} connectNulls activeDot={false}/>}

                        {/* ── lines, always above every band ── */}
                        {metrics.includes("cT")&&<Line yAxisId="l" type={curveType} dataKey="cT" stroke={SERIES.clicks.line} strokeWidth={3} dot={false} connectNulls activeDot={{r:6,fill:"#fff",stroke:SERIES.clicks.line,strokeWidth:2.5}}/>}
                        {metrics.includes("rT")&&<Line yAxisId="r" type={curveType} dataKey="rT" stroke={SERIES.revenue.line} strokeWidth={3} dot={false} connectNulls activeDot={{r:6,fill:"#fff",stroke:SERIES.revenue.line,strokeWidth:2.5}}/>}
                        {metrics.includes("cv")&&<Line yAxisId="r" type={curveType} dataKey="cvT" stroke={SERIES.cv.line} strokeWidth={3} dot={false} connectNulls activeDot={{r:6,fill:"#fff",stroke:SERIES.cv.line,strokeWidth:2.5}}/>}
                        {metrics.includes("cvr")&&<Line yAxisId="r" type={curveType} dataKey="cvrT" stroke={SERIES.cvr.line} strokeWidth={3} dot={false} connectNulls activeDot={{r:6,fill:"#fff",stroke:SERIES.cvr.line,strokeWidth:2.5}}/>}
                      </ComposedChart>
                    )}
                  </ResponsiveContainer>
                )}
              </div>

              {/* Legend only makes sense when a chart is on screen. */}
              {chartState === "chart" && (
                <div className="perf-leg">
                  {/* ── FIXED 2026-09-24 — THE LEGEND WAS LYING ─────────────
                      Every swatch here was a hand-typed colour that did not
                      match the chart. The "Yesterday Clicks" box was
                      rgba(189,213,246,0.75) while the band drawn on the chart
                      was rgba(147,197,253,0.55). Four different colours, four
                      mismatches. They now read the same SERIES object the
                      chart does, so a swatch is the colour, not a guess at
                      it — and future colour changes can never break this. */}
                  {metrics.includes("cT")&&<><span className="pleg"><span className="pleg-line" style={{background:SERIES.clicks.line}}></span>{perfSummary?.labelA||"Today"} Clicks</span><span className="pleg"><span className="pleg-box" style={{background:SERIES.clicks.band}}></span>{perfSummary?.labelB||"Yesterday"} Clicks</span></>}
                  {metrics.includes("rT")&&<><span className="pleg"><span className="pleg-line" style={{background:SERIES.revenue.line}}></span>{perfSummary?.labelA||"Today"} Revenue</span><span className="pleg"><span className="pleg-box" style={{background:SERIES.revenue.band}}></span>{perfSummary?.labelB||"Yesterday"} Revenue</span></>}
                  {metrics.includes("cv")&&<><span className="pleg"><span className="pleg-line" style={{background:SERIES.cv.line}}></span>{perfSummary?.labelA||"Today"} CV</span><span className="pleg"><span className="pleg-box" style={{background:SERIES.cv.band}}></span>{perfSummary?.labelB||"Yesterday"} CV</span></>}
                  {metrics.includes("cvr")&&<><span className="pleg"><span className="pleg-line" style={{background:SERIES.cvr.line}}></span>{perfSummary?.labelA||"Today"} CVR</span><span className="pleg"><span className="pleg-box" style={{background:SERIES.cvr.band}}></span>{perfSummary?.labelB||"Yesterday"} CVR</span></>}
                </div>
              )}

              <div className="perf-sum">
                {perfSummary ? (<>
                  {metrics.includes("cT") && <span>Clicks:&nbsp;<b>{perfSummary.clicks.a.toLocaleString()}</b>&nbsp;<span className="vs">vs</span>&nbsp;<b className="vo">{perfSummary.clicks.b.toLocaleString()}</b></span>}
                  {metrics.includes("cT") && (metrics.includes("rT")||metrics.includes("cv")||metrics.includes("cvr")) && <span className="pipe">|</span>}
                  {metrics.includes("rT") && <span>Revenue:&nbsp;<b>${perfSummary.revenue.a.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</b>&nbsp;<span className="vs">vs</span>&nbsp;<b className="vo">${perfSummary.revenue.b.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</b></span>}
                  {metrics.includes("cv") && <span>CV:&nbsp;<b>{perfSummary.conv.a.toLocaleString()}</b>&nbsp;<span className="vs">vs</span>&nbsp;<b className="vo">{perfSummary.conv.b.toLocaleString()}</b></span>}
                  {metrics.includes("cvr") && <span>CVR:&nbsp;<b>{perfSummary.cvr.a.toFixed(2)}%</b>&nbsp;<span className="vs">vs</span>&nbsp;<b className="vo">{perfSummary.cvr.b.toFixed(2)}%</b></span>}
                  {/* ADDED 2026-09-18: if a network couldn't answer, its
                      clicks are missing from these totals. Say so. */}
                  {perfSummary.skipped?.length > 0 && (
                    <span style={{color:"#B45309",fontSize:11.5,marginLeft:8}}>
                      ⚠ not included: {perfSummary.skipped.join(", ")}
                    </span>
                  )}
                </>) : chartState === "error" ? (
                  <span style={{color:"#B91C1C",fontSize:12}}>No totals — the fetch failed.</span>
                ) : (
                  <span style={{color:"var(--text-muted)",fontSize:12}}>Loading...</span>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
